import { clsx } from "clsx";

export type ShapeSpec = {
  shape?: string;
  color?: string;
  rotation?: number;
  fill?: "solid" | "outline";
  size?: "sm" | "md" | "lg";
};

const colors: Record<string, string> = {
  cyan: "text-cyan-200",
  blue: "text-blue-200",
  violet: "text-violet-200",
  emerald: "text-emerald-200",
  amber: "text-amber-200"
};

const sizes: Record<string, string> = {
  sm: "h-7 w-7",
  md: "h-10 w-10",
  lg: "h-14 w-14"
};

export function ShapeRenderer({ spec, className }: { spec: ShapeSpec; className?: string }) {
  const shape = spec.shape ?? "circle";
  const color = colors[spec.color ?? "cyan"] ?? colors.cyan;
  const size = sizes[spec.size ?? "md"] ?? sizes.md;
  const solid = spec.fill === "solid";
  const style = { transform: `rotate(${spec.rotation ?? 0}deg)` };

  if (shape === "question") return <span className={clsx("text-2xl font-bold text-cyan-100", className)}>?</span>;
  if (shape === "circle" || shape === "dot") {
    return <span className={clsx(size, color, shape === "dot" ? "scale-50" : "", solid ? "bg-current" : "border-4 border-current", "rounded-full drop-shadow-[0_0_14px_currentColor]", className)} style={style} />;
  }
  if (shape === "square" || shape === "diamond") {
    return <span className={clsx(size, color, solid ? "bg-current" : "border-4 border-current", "rounded-md drop-shadow-[0_0_14px_currentColor]", className)} style={{ ...style, transform: `rotate(${shape === "diamond" ? 45 : spec.rotation ?? 0}deg)` }} />;
  }
  if (shape === "line") {
    return <span className={clsx("h-1 w-12 rounded-full bg-current", color, className)} style={style} />;
  }

  const points: Record<string, string> = {
    triangle: "24 5 45 42 3 42",
    hexagon: "24 4 42 14 42 34 24 44 6 34 6 14",
    plus: "20 5 28 5 28 20 43 20 43 28 28 28 28 43 20 43 20 28 5 28 5 20 20 20",
    arrow: "24 4 44 24 32 24 32 43 16 43 16 24 4 24",
    star: "24 4 30 18 45 18 33 28 38 43 24 34 10 43 15 28 3 18 18 18"
  };

  return (
    <svg className={clsx(size, color, "drop-shadow-[0_0_14px_currentColor]", className)} viewBox="0 0 48 48" aria-hidden="true" style={style}>
      <polygon points={points[shape] ?? points.triangle} fill={solid ? "currentColor" : "none"} stroke="currentColor" strokeWidth="4" strokeLinejoin="round" />
    </svg>
  );
}
